import { Skeleton, Stack, Typography, styled } from "@mui/material";
import { Dispatch, FC, SetStateAction } from "react";
import * as apiClient from "../../api";
import { History, Responses } from "../../types";
import HistoryCard from "./HistoryCard";
import AddHistoryCard from "./AddHistoryCard";

const toArrayForm = (responses: Responses) => {
  const arr = [];

  for (const key in responses) {
    if (responses.hasOwnProperty(key)) {
      arr.push({
        questionId: key,
        value: responses[key],
      });
    }
  }

  return arr;
};

const LoadingCard = styled(Skeleton)({
  height: "3rem",
  margin: "0.5rem 0",
});

interface PatientHistoryTabProps {
  patientId: string;
  histories: History[];
  setHistories: Dispatch<SetStateAction<History[]>>;
  loaded: boolean;
}

const PatientHistoryTab: FC<PatientHistoryTabProps> = ({
  patientId,
  histories,
  setHistories,
  loaded,
}) => {
  const addPatientHistory = async (responses: Responses) => {
    const res = await apiClient.addPatientHistory(patientId, responses);
    const newPatientHistory = {
      historyId: res.data.historyId,
      createdAt: new Date().toISOString(),
      possibleDiagnoses: res.data.possibleDiagnoses,
      answers: toArrayForm(responses),
    } as History;

    const updatedHistories = [...histories, newPatientHistory];
    setHistories(updatedHistories);
  };

  const editPatientHistory = async (
    historyId: number,
    responses: Responses,
  ) => {
    const res = await apiClient.editPatientHistory(
      patientId,
      historyId,
      responses,
    );
    const updatedHistories = histories.map((history) =>
      history.historyId === historyId
        ? {
            ...history,
            possibleDiagnoses: res.data.possibleDiagnoses,
            answers: toArrayForm(responses),
          }
        : history,
    );
    setHistories(updatedHistories);
  };

  const deletePatientHistory = async (historyId: number) => {
    apiClient.deletePatientHistory(patientId, historyId);
    const updatedHistories = histories.filter(
      (history) => history.historyId !== historyId,
    );
    setHistories(updatedHistories);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6">Patient Histories</Typography>
        <AddHistoryCard addPatientHistory={addPatientHistory} />
      </Stack>
      {!loaded ? (
        <>
          <LoadingCard variant="rounded" />
          <LoadingCard variant="rounded" />
          <LoadingCard variant="rounded" />
        </>
      ) : histories.length ? (
        <>
          {histories.map((history) => (
            <HistoryCard
              key={history.historyId}
              patientId={patientId}
              history={history}
              editPatientHistory={editPatientHistory}
              deletePatientHistory={deletePatientHistory}
            />
          ))}
        </>
      ) : (
        <Typography textAlign="center">
          This patient has no recorded histories.
        </Typography>
      )}
    </Stack>
  );
};

export default PatientHistoryTab;
